import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";

// Turkish mobile: +90 5XX XXX XX XX  (10 digits after +90, or 11 starting with 0)
const TR_PHONE_RE = /^(\+?90)?0?5\d{9}$/;
const normPhone = (raw) => {
  const s = (raw || "").replace(/\D/g, "");
  if (!s) return "";
  if (s.startsWith("90") && s.length === 12) return "+" + s;
  if (s.startsWith("0") && s.length === 11) return "+90" + s.slice(1);
  if (s.length === 10 && s.startsWith("5")) return "+90" + s;
  return raw.trim();
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    role: "user",
    name: "",
    surname: "",
    phone: "",
    email: "",
    password: "",
    company_name: "",
    address: "",
  });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.role === "business_owner" && (!form.company_name.trim() || !form.address.trim())) {
      toast.error("Firma adı ve adres zorunludur");
      return;
    }
    const phoneNorm = normPhone(form.phone);
    if (!TR_PHONE_RE.test(phoneNorm.replace("+", ""))) {
      toast.error("Geçerli bir Türkiye cep telefonu giriniz (örn. 05XX XXX XX XX)");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, phone: phoneNorm });
      toast.success("Kayıt başarılı");
      nav("/");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const isProvider = form.role === "business_owner";

  return (
    <div className="mx-auto max-w-md px-4 py-10">
      <div className="rounded-3xl border border-border bg-card p-6">
        <h1 className="text-2xl font-bold tracking-tight mb-1">Üye Ol</h1>
        <p className="text-sm text-muted-foreground mb-5">Hemen ücretsiz hesap oluşturun.</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Hesap türü</Label>
            <RadioGroup value={form.role} onValueChange={upd("role")} className="grid grid-cols-2 gap-2 mt-2">
              <label className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${form.role === "user" ? "border-primary bg-primary/10" : "border-border"}`}>
                <RadioGroupItem data-testid="role-user" value="user" />
                <span className="text-sm font-medium">Müşteri</span>
              </label>
              <label className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${form.role === "business_owner" ? "border-primary bg-primary/10" : "border-border"}`}>
                <RadioGroupItem data-testid="role-owner" value="business_owner" />
                <span className="text-sm font-medium">Hizmet Sağlayıcı</span>
              </label>
            </RadioGroup>
          </div>

          {isProvider && (
            <div>
              <Label>Firma Adı</Label>
              <Input data-testid="reg-company-name" value={form.company_name} onChange={upd("company_name")} className="mt-1 h-11 rounded-xl" required />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{isProvider ? "Yetkili Adı" : "Ad"}</Label>
              <Input data-testid="reg-name" value={form.name} onChange={upd("name")} className="mt-1 h-11 rounded-xl" required />
            </div>
            <div>
              <Label>{isProvider ? "Yetkili Soyadı" : "Soyad"}</Label>
              <Input data-testid="reg-surname" value={form.surname} onChange={upd("surname")} className="mt-1 h-11 rounded-xl" required />
            </div>
          </div>

          <div>
            <Label>Telefon</Label>
            <Input
              data-testid="reg-phone"
              type="tel"
              inputMode="tel"
              placeholder="05XX XXX XX XX"
              value={form.phone}
              onChange={upd("phone")}
              className="mt-1 h-11 rounded-xl"
              required
            />
          </div>

          <div>
            <Label>E-posta</Label>
            <Input data-testid="reg-email" type="email" value={form.email} onChange={upd("email")} className="mt-1 h-11 rounded-xl" required />
          </div>

          {isProvider && (
            <div>
              <Label>Adres</Label>
              <Textarea data-testid="reg-address" value={form.address} onChange={upd("address")} className="mt-1 rounded-xl" rows={2} required />
            </div>
          )}

          <div>
            <Label>Şifre (min 6 karakter)</Label>
            <Input data-testid="reg-password" type="password" minLength={6} value={form.password} onChange={upd("password")} className="mt-1 h-11 rounded-xl" required />
          </div>

          <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full h-11 rounded-full press">
            {loading ? "Yükleniyor..." : "Üye Ol"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Hesabınız var mı? <Link to="/giris" className="text-primary font-medium underline">Giriş Yapın</Link>
        </p>
      </div>
    </div>
  );
}
